import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import noop from '../../../utils/noop';

/** tracks if the image has had it's src file loaded and optionally
 *
 *  @example
 *  const { isLoading, onSrcLoadFinish } = useImgIsLoading(
 *    previewFile.src,
 *    () => console.log('finally loaded')
 *  );
 *
 *  // ...
 *  <img
 *    ...
 *    src={previewFile.src}
 *    className={classnames(isLoading && styles['loading'])}
 *    onLoad={onSrcLoadFinish}
 *    onError={onSrcLoadFinish}
 *    ... />
 */
export function useImgIsLoading(
  /** img element to monitor for loading */
  imgRef: React.RefObject<HTMLImageElement | null>,
  /** src used in img */
  src: string | undefined,
  /** fired when the image src is loaded (over the wire or from the cache) */
  onLoaded: () => void = noop
): {
  /** is the img fetching the image src file */
  isLoading: boolean;
  /** add this to both your image's onLoad and onError props */
  onSrcLoadFinish: (ev: ErrorEvent | SyntheticEvent) => void;
} {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const previousSrc = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (previousSrc.current !== src && !isLoading) {
      setIsLoading(true);
    }
    previousSrc.current = src;
  }, [src, isLoading]);

  return {
    // safari handles cached images a bit different than ff or chrome so
    // we need to check the complete property or this update sizes on previously viewed
    isLoading: (isLoading && !imgRef.current?.complete) || previousSrc.current !== src,
    onSrcLoadFinish: () => {
      onLoaded();
      setIsLoading(false);
    },
  };
}

export default useImgIsLoading;
